import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { ApiService } from 'src/app/services/api.service';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';
import { SeoService } from 'src/app/services/seo.service';
import { PostItem, PostListingData } from 'src/app/types/post';
import { environment } from 'src/environments/environment';
import { CategoryItem } from 'src/app/types/category';
import { TagItem } from 'src/app/types/tag';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { removeTags } from 'src/app/utils/string-transform';
import { SEOSchema } from 'src/app/types/seo';
import { JsonLdService } from 'src/app/services/json-ld.service';

@Component({
  selector: 'app-listing',
  templateUrl: './blog-listing.page.html',
  styleUrls: ['./blog-listing.page.scss'],
})
export class BlogListingPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  lastItem: PostItem;
  amountPerPage: number = 6;
  xhrInProgress: boolean = false;
  hasMoreItems: boolean = true;
  selectedCategory: CategoryItem;
  selectedTag: TagItem;
  featuredItem: PostItem;
  sponsor = {
    details: 'Sponsored by Mr Price Home',
    logo: '',
  };
  sliceStart: number = 0;

  data: PostListingData = {
    title: 'Blogs',
    excerpt:
      environment.environment_name === 'capegate'
        ? 'Be inspired with the latest trends in fashion, décor, entertainment, health & beauty, tips & tricks and more by reading our blog.'
        : environment.environment_name == 'somerset'
        ? "Don't miss out on the latest trends in fashion, décor, entertainment, health and beauty by reading our blog."
        : environment.environment_name == 'glen'
        ? 'Be inspired with the latest trends in fashion, décor, entertainment, health & beauty, tips & tricks and more by reading our blog.'
        : environment.environment_name == 'rosebank'
        ? "Be inspired with the latest trends in fashion, décor, entertainment, health & beauty, tips & tricks and more by reading Rosebank mall's blog."
        : environment.environment_name == 'woodlands'
        ? 'Check out our website and be inspired with the latest trends in fashion, décor, entertainment, health & beauty, tips & tricks, and more by reading our blog'
        : "Be inspired with the latest trends in fashion, décor, entertainment, health & beauty, tips & tricks and more by reading our blog. Make sure that your finger is on the pulse by <a href='page/shopper-newsletter'>signing up to our monthly newsletter</a>.",
  };

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  constructor(
    private platform: Platform,
    private globalData: GlobalDataService,
    private API: ApiService,
    private seo: SeoService,
    private route: ActivatedRoute,
    public popoverController: PopoverController,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
  }

  /**
   * Fetch data according to page & category filter
   */
  fetchData() {
    this.API.GET(
      `public/blogs/${environment.mall_id}?featured=1&amount=${
        this.amountPerPage
      }${
        this.lastItem
          ? '&lastSlug=' +
            this.lastItem.slug +
            '&lastPublishedDate=' +
            this.lastItem.publishedAt
          : ''
      }${
        this.selectedCategory ? '&category=' + this.selectedCategory.slug : ''
      }${this.selectedTag ? '&tag=' + this.selectedTag.slug : ''}`
    ).subscribe({
      next: (response) => {
        if (!this.data?.items || this.data?.items.length < 1) {
          let items = response.results;
          if (response.results[0]?.featured) {
            this.featuredItem = response.results[0];
            this.sliceStart = 1;
          } else {
            this.featuredItem = null;
            this.sliceStart = 0;
          }

          this.data = {
            ...this.data,
            categories: response.categories,
            items: items,
          };
        } else {
          const extendedItems = this.data.items.concat(response.results);
          this.data.items = extendedItems;
        }

        if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
          this.selectedCategory = response.categories.filter((category) => {
            return category.slug === this.selectedCategory.slug;
          })[0];
        }

        if (this.selectedCategory) this.setCategorySeo();

        this.hasMoreItems = !(response.results.length < this.amountPerPage);

        this.xhrInProgress = false;
      },
      error: (response) => {},
    });
  }

  /**
   * Load more results
   */
  loadMore() {
    if (!this.xhrInProgress) {
      this.xhrInProgress = true;

      this.lastItem = this.data.items[this.data.items.length - 1];

      this.fetchData();
    }
  }

  /**
   * Reset category filter
   */
  resetCategories() {
    delete this.data.items;
    this.selectedCategory = null;
    this.selectedTag = null;
    this.lastItem = null;
    this.fetchData();
    window?.history.pushState({}, '', '/blogs');

    // Set default Mall SEO data
    this.setMallSeo(this.globalData.getGlobalData());
  }

  /**
   * Present category filter popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data: this.data.categories,
      },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((popoverData) => {
      if (popoverData.data === 'all') {
        this.resetCategories();
      } else {
        const filterCategory = {
          title: popoverData.data?.title,
          slug: popoverData.data?.slug,
          seoTitle: popoverData.data?.seoTitle,
          seoDescription: popoverData.data?.seoDescription,
        };
        
        if (popoverData.data && this.selectedCategory != filterCategory) {
          this.selectedCategory = filterCategory;

          if (this.selectedCategory?.slug) {
            let description = popoverData.data.seoDescription;
            if (environment.environment_name == 'rosebank') {
              if (popoverData.data.slug == "must-haves") description = "Discover must-have items and trends at Rosebank Mall through our insightful blog.";
              if (popoverData.data.slug == "tips-and-tricks") description = "Explore our blog for helpful tips and tricks to enhance your shopping experience at Rosebank Mall.";
            } else if (environment.environment_name == 'clearwater') {
              if (popoverData.data.slug == "new-chcbc") description = "Stay updated with the latest news from Clearwater Mall. Discover exciting new arrivals, events, and promotions in our 'New' blog category.";
              if (popoverData.data.slug == "new-lt1on") description = "Explore the freshest content from Clearwater Mall. Our 'New' blog category brings you exciting updates, product launches, and lifestyle inspirations.";
              if (popoverData.data.slug == "must-haves") description = "Find your next obsession with Clearwater Mall's 'Must-Haves' blog. Explore trending products, fashion statements, and essential items for a stylish life.";
              if (popoverData.data.slug == "tips-and-tricks") description = "Unlock helpful advice and clever solutions with Clearwater Mall's 'Tips and Tricks' blog. Enhance your shopping, home, and lifestyle with our expert insights.";
              if (popoverData.data.slug == "food") description = "Satisfy your cravings with Clearwater Mall's 'Food' blog. Discover delicious dining options, recipes, and culinary highlights from our restaurants.";
              if (popoverData.data.slug == "fashion") description = "Stay on trend with Clearwater Mall's 'Fashion' blog. Get insights into the latest styles, seasonal must-haves, and fashion tips from our retailers.";
            } else if (environment.environment_name == 'glen') {
              if (popoverData.data.slug == "new-chcbc") description = "Stay on trend with fresh arrivals at The Glen Shopping Centre. Discover the latest in fashion, tech, and lifestyle from your favourite brands.";
              if (popoverData.data.slug == "new-lt1on") description = "Discover the latest in fashion, beauty, and tech at The Glen Shopping Centre. Explore new arrivals and fresh finds for every style and need.";
              if (popoverData.data.slug == "must-haves") description = "Check out The Glen's Must-Haves blog for the best shopping picks in Johannesburg South. Find fashion, accessories, gadgets, and lifestyle must-haves!";
              if (popoverData.data.slug == "tips-and-tricks") description = "Unlock the best shopping secrets at The Glen Shopping Centre. Explore our 'Tips and Tricks' blog for expert advice on style, tech, and more.";
              if (popoverData.data.slug == "food") description = "Explore food tips, dining ideas, and delicious updates on our Food blog at The Glen Shopping Centre in Johannesburg South.";
              if (popoverData.data.slug == "fashion") description = "From everyday outfits to bold new looks—our fashion blog at The Glen Shopping Centre has you covered.";
              if (popoverData.data.slug == "family") description = "Stay updated with the latest family events and activities at The Glen Mall in Johannesburg South. Explore our blog for exciting family ideas!";
              if (popoverData.data.slug == "entertainment") description = "Looking for fun? The Glen Shopping Centre has you covered with great movies, games, and family-friendly entertainment in Johannesburg South.";
              if (popoverData.data.slug == "get-the-look") description = "Explore our 'Get the Look' blog at The Glen Shopping Centre. Find outfit ideas and style inspiration.";
            } else if (environment.environment_name == 'tablebay') {
              if (popoverData.data.slug == "new-chcbc") description = "Find beautiful home and decor stores at Table Bay Mall. Transform your space with stylish pieces.";
            }

            delete this.data.items;
            delete this.lastItem;
            this.selectedCategory = {
              ...popoverData.data,
              seoDescription: description
            };
            this.fetchData();
            window?.history.pushState(
              {},
              '',
              `/blogs/category/${this.selectedCategory.slug}`
            );
          }
        }
      }
    });

    return await popover.present();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();

    this.route.data.subscribe((response: any) => {
      let items = response.data.results;
      if (response.data.results[0]?.featured) {
        this.featuredItem = response.data.results[0];
        this.sliceStart = 1;
      }

      this.data = {
        ...this.data,
        categories: response.data.categories,
        items: items,
      };

      this.hasMoreItems = !(response.data.results.length < this.amountPerPage);

      let categorySlug = this.route.snapshot.paramMap.get('categorySlug');
      if (categorySlug) {
        this.selectedCategory = {
          slug: categorySlug,
        };
      } else {
        this.selectedCategory = null;
      }

      let tagSlug = this.route.snapshot.paramMap.get('tagSlug');
      if (tagSlug) {
        this.selectedTag = {
          slug: tagSlug,
        };
      } else {
        this.selectedTag = null;
      }

      if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
        for (let i = 0; i < this.data.categories.length; i++) {
          if (this.data.categories[i].slug === this.selectedCategory.slug) {
            this.selectedCategory = this.data.categories[i];
            break;
          } else if (this.data.categories[i].children) {
            for (let k = 0; k < this.data.categories[i].children.length; k++) {
              if (
                this.data.categories[i].children[k].slug ===
                this.selectedCategory.slug
              ) {
                this.selectedCategory = this.data.categories[i].children[k];
                break;
              }
            }
          }
        }
      }

      // Set SEO data
      if (this.selectedCategory) {
        this.setCategorySeo();
      } else {
        this.setMallSeo(initialMallData);
      }
    });
  }

  setMallSeo(mallData) {
    // Set Meta & Social tags
    if (!this.cordova) {
      let schemaData = { ...this.schema };

      if (this.data?.items?.length > 0) {
        let itemListElement = [];

        for (let index = 0; index < this.data.items.length; index++) {
          itemListElement.push({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Blog',
              name: this.data.items[index].title,
              image: this.data.items[index].featuredImage,

              brand: {
                logo: this.data.items[index].featuredImage,
                name: this.data.items[index].title,
              },
            },
          });
        }

        schemaData = {
          ...schemaData,
          numberOfItems: this.data.items.length,
          itemListElement: itemListElement,
        };
      }

      // Title tag
      const seoTitle =
        'Blogs | ' + environment.environment_title + ' Shopping Centre';
      this.seo.setTitle(seoTitle);
      schemaData = {
        ...schemaData,
        name: seoTitle,
      };

      // Description tag
      const seoData = {
        title: seoTitle,
        description: removeTags(this.data.excerpt),
        keywords: mallData.general.metaKeywords + ' blog articles',
        socialImage: mallData.social.openGraphImage,
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);

      schemaData = {
        ...schemaData,
        description: seoData.description,
      };

      this.schema = { ...this.schema, ...schemaData };
      this.jsonLdService.insertSchema(this.schema);
    }
  }

  setCategorySeo() {
    let schemaData = { ...this.schema };

    let description = this.selectedCategory.seoDescription;
    if (environment.environment_name == 'rosebank') {
      if (this.selectedCategory.slug == "must-haves") description = "Discover must-have items and trends at Rosebank Mall through our insightful blog.";
      if (this.selectedCategory.slug == "tips-and-tricks") description = "Explore our blog for helpful tips and tricks to enhance your shopping experience at Rosebank Mall.";
    } else if (environment.environment_name == 'clearwater') {
      if (this.selectedCategory.slug == "new-chcbc") description = "Stay updated with the latest news from Clearwater Mall. Discover exciting new arrivals, events, and promotions in our 'New' blog category.";
      if (this.selectedCategory.slug == "new-lt1on") description = "Explore the freshest content from Clearwater Mall. Our 'New' blog category brings you exciting updates, product launches, and lifestyle inspirations.";
      if (this.selectedCategory.slug == "must-haves") description = "Find your next obsession with Clearwater Mall's 'Must-Haves' blog. Explore trending products, fashion statements, and essential items for a stylish life.";
      if (this.selectedCategory.slug == "tips-and-tricks") description = "Unlock helpful advice and clever solutions with Clearwater Mall's 'Tips and Tricks' blog. Enhance your shopping, home, and lifestyle with our expert insights.";
      if (this.selectedCategory.slug == "food") description = "Satisfy your cravings with Clearwater Mall's 'Food' blog. Discover delicious dining options, recipes, and culinary highlights from our restaurants.";
      if (this.selectedCategory.slug == "fashion") description = "Stay on trend with Clearwater Mall's 'Fashion' blog. Get insights into the latest styles, seasonal must-haves, and fashion tips from our retailers.";
    } else if (environment.environment_name == 'glen') {
      if (this.selectedCategory.slug == "new-chcbc") description = "Stay on trend with fresh arrivals at The Glen Shopping Centre. Discover the latest in fashion, tech, and lifestyle from your favourite brands.";
      if (this.selectedCategory.slug == "new-lt1on") description = "Discover the latest in fashion, beauty, and tech at The Glen Shopping Centre. Explore new arrivals and fresh finds for every style and need.";
      if (this.selectedCategory.slug == "must-haves") description = "Check out The Glen's Must-Haves blog for the best shopping picks in Johannesburg South. Find fashion, accessories, gadgets, and lifestyle must-haves!";
      if (this.selectedCategory.slug == "tips-and-tricks") description = "Unlock the best shopping secrets at The Glen Shopping Centre. Explore our 'Tips and Tricks' blog for expert advice on style, tech, and more.";
      if (this.selectedCategory.slug == "food") description = "Explore food tips, dining ideas, and delicious updates on our Food blog at The Glen Shopping Centre in Johannesburg South.";
      if (this.selectedCategory.slug == "fashion") description = "From everyday outfits to bold new looks—our fashion blog at The Glen Shopping Centre has you covered.";
      if (this.selectedCategory.slug == "family") description = "Stay updated with the latest family events and activities at The Glen Mall in Johannesburg South. Explore our blog for exciting family ideas!";
      if (this.selectedCategory.slug == "entertainment") description = "Looking for fun? The Glen Shopping Centre has you covered with great movies, games, and family-friendly entertainment in Johannesburg South.";
      if (this.selectedCategory.slug == "get-the-look") description = "Explore our 'Get the Look' blog at The Glen Shopping Centre. Find outfit ideas and style inspiration.";
    } else if (environment.environment_name == 'tablebay') {
      if (this.selectedCategory.slug == "new-chcbc") description = "Find beautiful home and decor stores at Table Bay Mall. Transform your space with stylish pieces.";
    }

    const seoData = {
      title:
        'Blogs | ' +
        (this.selectedCategory.seoTitle &&
        this.selectedCategory.seoTitle.length > 0
          ? this.selectedCategory.seoTitle
          : this.selectedCategory.title) +
        ' | ' +
        environment.environment_title +
        ' Shopping Centre',
      description: description,
    };
    
    this.seo.setTitle(seoData.title);

    schemaData = {
      ...schemaData,
      name: seoData.title,
    };
    this.seo.setMeta(seoData);
    this.seo.setSEOMeta(seoData);

    this.schema = { ...this.schema, ...schemaData };

    this.jsonLdService.insertSchema(this.schema);
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
